import { useState } from "react";
import { Image as ImagePlus, X } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { meQuery } from "@/api/queries/me";
import { useCreatePost } from "@/api/queries/posts";
import { Img } from "@/components/media/Img";
import { Avatar, Button, Card, IconButton, Menu } from "@/components/ui";
import { useComposerStore } from "@/stores/composer";

const EMPTY_DRAFT = { text: "", mediaKey: null } as const;

export const selectComposerDraft = (scope: string) => (state: { drafts: Record<string, { text: string; mediaKey: string | null }> }) =>
  state.drafts[scope];

const IMAGE_OPTIONS = [
  { key: "f-nature-kit-v2", label: "Forest village" },
  { key: "f-lobby-progress", label: "Lobby in progress" },
  { key: "f-showcase-castle", label: "Castle showcase" },
  { key: "f-studio-office", label: "Studio office" },
];

export function PostComposer({
  scope,
  placeholder = "What's on your mind, builder?",
  communitySlug,
}: {
  scope: string;
  placeholder?: string;
  communitySlug?: string;
}) {
  const { data: me } = useQuery(meQuery());
  const [focused, setFocused] = useState(false);
  const storedDraft = useComposerStore(selectComposerDraft(scope));
  const draft = storedDraft ?? EMPTY_DRAFT;
  const setText = useComposerStore((state) => state.setText);
  const setMedia = useComposerStore((state) => state.setMedia);
  const clear = useComposerStore((state) => state.clear);
  const create = useCreatePost();
  const rows = focused || draft.text.length > 0 ? 3 : 1;

  return (
    <Card className="space-y-3 rounded-xl bg-surface-1 p-2.5">
      <div className="flex items-center gap-3">
        {me && <Avatar size="lg" imageKey={me.avatar.key} alt={me.displayName} />}
        <textarea
          rows={rows}
          value={draft.text}
          onChange={(event) => setText(scope, event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          className={
            "min-h-8 flex-1 resize-none rounded-sm border-0 bg-transparent p-1.5 text-body-lg outline-none placeholder:text-text-muted"
          }
        />
      </div>
      {draft.mediaKey && (
        <div className="relative w-32">
          <Img
            imageKey={draft.mediaKey}
            alt="Selected post image"
            aspect="4/3"
            sizes="128px"
            className="rounded-sm"
          />
          <IconButton
            label="Remove image"
            icon={<X size={16} />}
            className="absolute right-1 top-1"
            onClick={() => setMedia(scope, null)}
          />
        </div>
      )}
      {(focused || draft.text.length > 0 || draft.mediaKey) && <div className="flex items-center justify-between">
        <Menu
          trigger={<IconButton label="Add image" icon={<ImagePlus size={18} />} />}
          items={IMAGE_OPTIONS.map(({ key, label }) => ({
            label,
            onSelect: () => setMedia(scope, key),
          }))}
        />
        <Button
          size="sm"
          disabled={!draft.text.trim()}
          onClick={() => {
            create.mutate({ body: draft.text, mediaKey: draft.mediaKey ?? undefined, communitySlug });
            clear(scope);
          }}
        >
          Post
        </Button>
      </div>}
    </Card>
  );
}
